import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import ConfirmDialog from '../ConfirmDialog'

// Компонент DatePicker с кастомным календарем для мобильных
const MobileDatePicker = ({ label, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [tempDate, setTempDate] = useState(value)

    // Закрытие календаря при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen && !event.target.closest('.mobile-date-picker-container')) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const formatDisplayDate = (date) => {
        return date.format('MM/DD/YYYY')
    }

    const handleDateSelect = (selectedDate) => {
        setTempDate(selectedDate)
        onChange(selectedDate)
        setIsOpen(false)
    }

    const handleClear = () => {
        const clearedDate = dayjs()
        setTempDate(clearedDate)
        onChange(clearedDate)
        setIsOpen(false)
    }

    const handleToday = () => {
        const today = dayjs()
        setTempDate(today)
        onChange(today)
        setIsOpen(false)
    }

    const getCalendarDays = () => {
        const startOfMonth = tempDate.startOf('month')
        const endOfMonth = tempDate.endOf('month')
        const startOfCalendar = startOfMonth.startOf('week')
        const endOfCalendar = endOfMonth.endOf('week')

        const days = []
        let current = startOfCalendar

        while (current.isBefore(endOfCalendar, 'day') || current.isSame(endOfCalendar, 'day')) {
            days.push({
                date: current,
                isCurrentMonth: current.isSame(tempDate, 'month'),
                isSelected: current.isSame(value, 'day'),
                isToday: current.isSame(dayjs(), 'day')
            })
            current = current.add(1, 'day')
        }

        return days
    }

    const navigateMonth = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'month')
            : tempDate.add(1, 'month')
        setTempDate(newDate)
    }

    const navigateYear = (direction) => {
        const newDate = direction === 'prev' 
            ? tempDate.subtract(1, 'year')
            : tempDate.add(1, 'year')
        setTempDate(newDate)
    }

    return (
        <div className="mobile-filter-group mobile-date-picker-container" style={{ position: 'relative' }}>
            <label className="mobile-filter-label">{label}</label>
            <div 
                className="mobile-filter-input"
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <span>{formatDisplayDate(value)}</span>
                <span style={{ color: 'var(--primary-orange)' }}>📅</span>
            </div>

            {isOpen && (
                <>
                    {/* Затемненный фон */}
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9999
                    }} onClick={() => setIsOpen(false)} />
                    
                    {/* Календарь */}
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10000,
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-md)',
                        boxShadow: 'var(--shadow-card)',
                        width: '90vw',
                        maxWidth: '320px',
                        maxHeight: '80vh',
                        overflow: 'auto'
                    }}>
                        {/* Заголовок календаря */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 'var(--spacing-sm)',
                            gap: '4px'
                        }}>
                            <button
                                onClick={() => navigateYear('prev')}
                                style={{ 
                                    background: 'none',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '4px 6px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    minWidth: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                                onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                            >
                                ⟪
                            </button>
                            <button
                                onClick={() => navigateMonth('prev')}
                                style={{ 
                                    background: 'none',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '4px 6px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    minWidth: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                                onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                            >
                                ‹
                            </button>
                            <span style={{
                                fontSize: 'var(--text-sm)',
                                fontWeight: '600',
                                color: 'var(--text-light)',
                                flex: 1,
                                textAlign: 'center'
                            }}>
                                {tempDate.format('MMMM YYYY')}
                            </span>
                            <button
                                onClick={() => navigateMonth('next')}
                                style={{ 
                                    background: 'none',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '4px 6px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    minWidth: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                                onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                            >
                                ›
                            </button>
                            <button
                                onClick={() => navigateYear('next')}
                                style={{ 
                                    background: 'none',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '4px 6px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    minWidth: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                                onMouseLeave={(e) => e.target.style.borderColor = 'var(--light-gray)'}
                            >
                                ⟫
                            </button>
                        </div>

                        {/* Дни недели */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '2px',
                            marginBottom: 'var(--spacing-sm)'
                        }}>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                                <div key={day} style={{
                                    padding: '6px 2px',
                                    textAlign: 'center',
                                    fontSize: 'var(--text-xs)',
                                    fontWeight: '600',
                                    color: 'var(--text-muted)'
                                }}>
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Календарная сетка */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '2px'
                        }}>
                            {getCalendarDays().map((day, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleDateSelect(day.date)}
                                    style={{
                                        padding: '6px 2px',
                                        border: 'none',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: 'var(--text-xs)',
                                        cursor: 'pointer',
                                        background: day.isSelected 
                                            ? 'var(--primary-orange)' 
                                            : day.isToday 
                                                ? 'var(--dark-gray)' 
                                                : 'transparent',
                                        color: day.isSelected 
                                            ? 'var(--white)' 
                                            : !day.isCurrentMonth 
                                                ? 'var(--text-muted)' 
                                                : day.isToday 
                                                    ? 'var(--primary-orange)' 
                                                    : 'var(--text-light)',
                                        fontWeight: day.isSelected || day.isToday ? '600' : '400',
                                        transition: 'var(--transition-normal)'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!day.isSelected) {
                                            e.target.style.backgroundColor = 'var(--dark-gray)'
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!day.isSelected) {
                                            e.target.style.backgroundColor = day.isToday ? 'var(--dark-gray)' : 'transparent'
                                        }
                                    }}
                                >
                                    {day.date.format('D')}
                                </button>
                            ))}
                        </div>

                        {/* Кнопки действий */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 'var(--spacing-sm)',
                            paddingTop: 'var(--spacing-sm)',
                            borderTop: '1px solid var(--light-gray)',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <button
                                onClick={handleClear}
                                style={{
                                    background: 'none',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    flex: 1,
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                    e.target.style.color = 'var(--primary-orange)'
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.borderColor = 'var(--light-gray)'
                                    e.target.style.color = 'var(--text-light)'
                                }}
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleToday}
                                style={{
                                    background: 'var(--primary-orange)',
                                    border: '1px solid var(--primary-orange)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--white)',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    fontSize: 'var(--text-xs)',
                                    flex: 1,
                                    transition: 'var(--transition-normal)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = 'var(--dark-orange)'
                                    e.target.style.borderColor = 'var(--dark-orange)'
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'var(--primary-orange)'
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                }}
                            >
                                Today
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

// Компонент пагинации для мобильных
const MobilePaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-xs)',
            flexWrap: 'wrap',
            justifyContent: 'center'
        }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-xs)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center'
            }}>
                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, safeCount)} of {safeCount}
            </span>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardArrowRight style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    transition: 'var(--transition-normal)',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
                onMouseEnter={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--primary-orange)'
                        e.target.style.borderColor = 'var(--primary-orange)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!e.target.disabled) {
                        e.target.style.background = 'var(--dark-gray)'
                        e.target.style.borderColor = 'var(--light-gray)'
                    }
                }}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
            </button>
        </div>
    )
}

const MobileHistoryTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [historyList, setHistoryList] = useState([])
    const [allHistoryData, setAllHistoryData] = useState([])
    const [startDate, setStartDate] = useState(() => dayjs().subtract(7, 'day'))
    const [endDate, setEndDate] = useState(() => dayjs())
    const [showFilters, setShowFilters] = useState(false)
    const [ipFilter, setIpFilter] = useState('')
    const [noteFilter, setNoteFilter] = useState('')

    //history purchase
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [selectedRow, setSelectedRow] = useState([])
    const [afterBuy, setAfterBuy] = useState(false)
    const [errorGetAgentInfo, setErrorGetAgentInfo] = useState(false)
    const [loadingRowId, setLoadingRowId] = useState(null)
    
    //notes
    const [notes, setNotes] = useState({})

    // CSS стили для анимации спиннера
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Анимация спиннера */
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - historyList?.items?.length) : 0

    const handleChangePage = React.useCallback((event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }, [])

    const handleChangeRowsPerPage = React.useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }, [])

    // Загрузка данных с сервера
    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD 00:00:00')
            const dateEndFormated = endDate.format('YYYY-MM-DD 23:59:59')
            const historyResponse = await props.apiClient.setToken(getAccessTokenSilently()).getUserHistory(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setAllHistoryData(historyResponse)
          } catch (e) {
          }
      }
      callAsync()   
    },[startDate, endDate, page, rowsPerPage])

    // Функция для фильтрации данных по IP и запискам
    const filterHistoryByIpAndNotes = React.useCallback((data, ipFilter, noteFilter, notesData) => {
      if (!data?.items) {
        return data
      }
      
      let filteredItems = data.items
      
      // Фильтрация по IP
      if (ipFilter && ipFilter.trim() !== '') {
        filteredItems = filteredItems.filter(item => 
          item.ip && item.ip.toLowerCase().includes(ipFilter.toLowerCase())
        )
      }
      
      // Фильтрация по запискам
      if (noteFilter && noteFilter.trim() !== '') {
        filteredItems = filteredItems.filter(item => {
          const note = notesData[item.agentID] || item.note || ''
          return note.toLowerCase().includes(noteFilter.toLowerCase())
        })
      }
      
      return {
        ...data,
        items: filteredItems,
        total: filteredItems.length
      }
    }, [])

    // Применение фильтрации по IP и запискам
    useEffect(() => {
      const filteredData = filterHistoryByIpAndNotes(allHistoryData, ipFilter, noteFilter, notes)
      setHistoryList(filteredData)
    }, [allHistoryData, ipFilter, noteFilter, notes, filterHistoryByIpAndNotes])

    const handleSetStartDate = React.useCallback((date) => {
      setStartDate(date)
    }, [])

    const handleSetEndDate = React.useCallback((date) => {
      setEndDate(date)
    }, [])

    const handleIpFilterChange = React.useCallback((event) => {
      setIpFilter(event.target.value)
      setPage(0) // Сброс на первую страницу при изменении фильтра
    }, [])

    const handleNoteFilterChange = React.useCallback((event) => {
      setNoteFilter(event.target.value)
      setPage(0) // Сброс на первую страницу при изменении фильтра
    }, [])

    const handleButtonClick = React.useCallback(async(row) => {
       try {
          setLoadingRowId(row?.agentID)
          const agentData = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(row?.agentID)
          if(agentData?.id == null || agentData?.id == undefined){
              throw 'error'
          }

          setSelectedRow(agentData)
          setConfirmOpen(true)
       } catch(e){
          setErrorGetAgentInfo(true)
       } finally {
          setLoadingRowId(null)
       }
    }, [])

    const handleCloseGetAgentInfoError = React.useCallback(() => {
      setErrorGetAgentInfo(false)
    }, [])


    return (
        <div style={{
            width: '100%',
            height: '100%',
            padding: 'var(--spacing-sm)',
            background: 'var(--black)',
            overflow: 'auto'
        }}>
            
            {/* Мобильные фильтры по дате */}
            <div className="mobile-filters">
                <div 
                    className="mobile-filters-header"
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <span className="mobile-filters-title">Date Filters</span>
                    <span style={{ 
                        color: 'var(--text-muted)',
                        transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ▼
                    </span>
                </div>
                
                {showFilters && (
                    <div className="mobile-filters-content">
                        <div className="mobile-filters-grid">
                            <MobileDatePicker 
                                label="From" 
                                value={startDate} 
                                onChange={handleSetStartDate}
                            />
                            <MobileDatePicker 
                                label="To" 
                                value={endDate} 
                                onChange={handleSetEndDate}
                            />
                        </div>
                        <div className="mobile-filter-group" style={{ marginTop: 'var(--spacing-md)' }}>
                            <label className="mobile-filter-label">
                                Search by IP
                                {ipFilter && (
                                    <span style={{
                                        marginLeft: '8px',
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--primary-orange)',
                                        fontWeight: '500'
                                    }}>
                                        ({historyList?.total || 0} found)
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                className={`mobile-filter-input ${ipFilter ? 'active' : ''}`}
                                placeholder="Enter IP address..."
                                value={ipFilter}
                                onChange={handleIpFilterChange}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: ipFilter ? '1px solid var(--primary-orange)' : '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '12px 16px',
                                    fontSize: 'var(--text-sm)',
                                    transition: 'var(--transition-normal)',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 152, 0, 0.2)'
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = ipFilter ? 'var(--primary-orange)' : 'var(--light-gray)'
                                    e.target.style.boxShadow = 'none'
                                }}
                            />
                        </div>
                        <div className="mobile-filter-group" style={{ marginTop: 'var(--spacing-md)' }}>
                            <label className="mobile-filter-label">
                                Search by Notes
                                {noteFilter && (
                                    <span style={{
                                        marginLeft: '8px',
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--primary-orange)',
                                        fontWeight: '500'
                                    }}>
                                        ({historyList?.total || 0} found)
                                    </span>
                                )}
                            </label>
                            <input
                                type="text"
                                className={`mobile-filter-input ${noteFilter ? 'active' : ''}`}
                                placeholder="Enter note text..."
                                value={noteFilter}
                                onChange={handleNoteFilterChange}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: noteFilter ? '1px solid var(--primary-orange)' : '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '12px 16px',
                                    fontSize: 'var(--text-sm)',
                                    transition: 'var(--transition-normal)',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--primary-orange)'
                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 152, 0, 0.2)'
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = noteFilter ? 'var(--primary-orange)' : 'var(--light-gray)'
                                    e.target.style.boxShadow = 'none'
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            {historyList?.items?.length > 0 ? (
                <div>
                    {/* Мобильные карточки истории */}
                    <div style={{ padding: '0' }}>
                        {historyList?.items?.map((row, index) => (
                            <div
                                key={index}
                                style={{
                                    background: 'var(--medium-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-md)',
                                    padding: 'var(--spacing-md)',
                                    marginBottom: 'var(--spacing-sm)',
                                    cursor: 'pointer',
                                    transition: 'var(--transition-normal)'
                                }}
                            >
                                {/* Заголовок карточки */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: 'var(--spacing-md)'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-lg)',
                                            fontWeight: '600',
                                            color: 'var(--text-light)',
                                            marginBottom: 'var(--spacing-xs)'
                                        }}>
                                            {row?.ip}
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-muted)'
                                        }}>
                                            {row?.loc?.cc} • {row?.loc?.city}, {row?.loc?.reg}
                                        </div>
                                    </div>
                                    <button 
                                        className="btn btn-primary btn-small"
                                        onClick={() => handleButtonClick(row)}
                                        disabled={loadingRowId === row?.agentID}
                                        style={{ 
                                            minWidth: '60px',
                                            opacity: loadingRowId === row?.agentID ? 0.7 : 1,
                                            cursor: loadingRowId === row?.agentID ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {loadingRowId === row?.agentID ? (
                                            <div style={{
                                                width: '14px',
                                                height: '14px',
                                                border: '2px solid var(--light-gray)',
                                                borderTop: '2px solid var(--primary-orange)',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }} />
                                        ) : (
                                            'Buy'
                                        )}
                                    </button>
                                </div>

                                {/* Детали истории */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 'var(--spacing-md)',
                                    marginBottom: 'var(--spacing-md)'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: '4px',
                                            fontWeight: '500'
                                        }}>
                                            ZIP Code
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-light)'
                                        }}>
                                            {row?.loc?.zip || 'N/A'}
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: '4px',
                                            fontWeight: '500'
                                        }}>
                                            ISP
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            color: 'var(--text-light)',
                                            wordBreak: 'break-word'
                                        }}>
                                            {row?.loc?.isp?.replace(/(.{15})..+/, "$1…")}
                                        </div>
                                    </div>
                                </div>

                                {/* Дата */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: 'var(--spacing-sm)',
                                    background: 'var(--dark-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--light-gray)'
                                }}>
                                    <div>
                                        <div style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: '2px'
                                        }}>
                                            Purchased
                                        </div>
                                        <div style={{
                                            fontSize: 'var(--text-sm)',
                                            fontWeight: '600',
                                            color: 'var(--primary-orange)'
                                        }}>
                                            {moment(row.paidUntil).format('MMM DD, YYYY')}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--text-xs)',
                                        color: 'var(--text-muted)',
                                        textAlign: 'right'
                                    }}>
                                        <div>{moment(row.paidUntil).format('h:mm A')}</div>
                                    </div>
                                </div>

                                {/* Заметка */}
                                <div style={{
                                    marginTop: 'var(--spacing-sm)',
                                    padding: 'var(--spacing-sm)',
                                    background: 'var(--dark-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--light-gray)'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: 'var(--spacing-xs)'
                                    }}>
                                        <span style={{
                                            fontSize: 'var(--text-xs)',
                                            color: 'var(--text-muted)',
                                            fontWeight: '500'
                                        }}>
                                            📝 Note
                                        </span>
                                    </div>
                                    <div 
                                        style={{
                                            fontSize: 'var(--text-sm)',
                                            color: notes[row?.agentID || row?.leaseID] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                                            fontStyle: notes[row?.agentID || row?.leaseID] || row?.note ? 'normal' : 'italic',
                                            lineHeight: '1.4',
                                            cursor: (notes[row?.agentID || row?.leaseID] || row?.note) ? 'help' : 'default'
                                        }}
                                        title={(notes[row?.agentID || row?.leaseID] || row?.note || 'No note added').replace(/(.{20})/g, '$1\n')}
                                    >
                                        {(notes[row?.agentID || row?.leaseID] || row?.note || 'No note added').length > 10 
                                            ? (notes[row?.agentID || row?.leaseID] || row?.note || 'No note added').substring(0, 10) + '...'
                                            : (notes[row?.agentID || row?.leaseID] || row?.note || 'No note added')
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Мобильная пагинация */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Строка с навигацией */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)'
                        }}>
                            <MobilePaginationActions 
                                page={page}
                                rowsPerPage={rowsPerPage}
                                count={historyList?.total || 0}
                                onPageChange={handleChangePage}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--spacing-2xl)',
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-card)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: 'var(--spacing-lg)'
                    }}>
                        📋
                    </div>
                    <h3 style={{
                        color: 'var(--text-light)',
                        fontSize: 'var(--text-lg)',
                        fontWeight: '600',
                        margin: '0 0 var(--spacing-sm) 0'
                    }}>
                        No history records
                    </h3>
                    <p style={{
                        color: 'var(--text-muted)',
                        fontSize: 'var(--text-sm)',
                        margin: '0',
                        lineHeight: '1.5'
                    }}>
                        You don't have any history records for the selected period.
                    </p>
                </div>
            )}
            
            {/* Диалог подтверждения покупки */}
            <ConfirmDialog 
                open={confirmOpen} 
                setConfirmOpen={setConfirmOpen} 
                selectedProxy={selectedRow} 
                apiClient={props.apiClient} 
                setAfterBuy={setAfterBuy}
            />
            
            {/* Уведомление об ошибке */}
            {errorGetAgentInfo && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#FF9800',
                    color: 'var(--white)',
                    padding: '12px 24px',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-card)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-sm)',
                    animation: 'fadeInDown 0.3s ease-out'
                }}>
                    <span>⚠️</span>
                    <span>Couldn't get proxy info. Please try again later.</span>
                    <button
                        onClick={handleCloseGetAgentInfoError}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            fontSize: 'var(--text-lg)',
                            marginLeft: 'var(--spacing-sm)'
                        }}
                    >
                        ×
                    </button>
                    <style>{`
                        @keyframes fadeInDown {
                            from {
                                opacity: 0;
                                transform: translateX(-50%) translateY(-20px);
                            }
                            to {
                                opacity: 1;
                                transform: translateX(-50%) translateY(0);
                            }
                        }
                    `}</style>
                </div>
            )}
        </div>
    )
}

export default MobileHistoryTable
